// Activity log: every change to the books, written by the database itself
// (acc_audit_log is append-only and hash-chained). Read-only screen.
import { useState, useEffect, useMemo } from "react";
import { Notice, Select, Button, StatusBadge, SkeletonTable } from "./kit/components";
import { T, text, fmtDateTime } from "./kit/tokens";
import { loadActivity } from "./api";
import { bsMonthLabel } from "./bs";
import { SectionCard, errText } from "./shared";

const AREA = {
    acc_settings: "Company & activation", acc_bs_calendar: "Calendar", acc_fiscal_years: "Fiscal years", acc_periods: "Fiscal years",
    acc_accounts: "Chart of accounts", acc_account_mappings: "Chart of accounts", acc_parties: "People & companies",
    acc_party_roles: "People & companies", acc_tax_codes: "Taxes", acc_tax_rates: "Taxes", acc_sequences: "Journal",
    acc_journal_entries: "Journal", acc_journal_lines: "Journal", acc_attachments: "Attachments",
};
const VERB = { insert: "Added", update: "Changed", delete: "Removed", post: "Posted", reverse: "Reversed", lock: "Locked", unlock: "Unlocked", hide: "Hid" };
const FIELD = {
    company_name: "company name", pan: "PAN", vat_registered: "VAT status", vat_effective_from: "VAT date", books_start_date: "books start date",
    enabled: "Accounts on/off", date_display: "date display", name: "name", active: "active", description: "description", cash_flow_class: "cash-flow group",
    verified: "verification", verified_ref: "verification source", days: "days", ad_start: "start date", contact: "contact", rate_percent: "rate",
    effective_to: "end date", status: "status",
};
const NOISE = new Set(["updated_at", "verified_at", "verified_by", "locked_at", "locked_by", "lock_reason", "hidden_at", "hidden_by", "hidden_reason", "posted_at", "posted_by", "content_hash", "entry_no", "total_amount"]);

function subject(row) {
    const v = row.after || row.before || {};
    switch (row.table_name) {
        case "acc_accounts": return `account ${v.code} ${v.name}`;
        case "acc_account_mappings": return `automation mapping "${v.role}"`;
        case "acc_parties": return v.name;
        case "acc_party_roles": return `role "${v.role}"`;
        case "acc_fiscal_years": return `fiscal year ${v.label}`;
        case "acc_periods": return bsMonthLabel(v.bs_year, v.bs_month);
        case "acc_bs_calendar": return `calendar month ${bsMonthLabel(v.bs_year, v.bs_month)}`;
        case "acc_tax_codes": return `tax code ${v.code}`;
        case "acc_tax_rates": return `tax rate ${Number(v.rate_percent)}%`;
        case "acc_settings": return "company settings";
        case "acc_sequences": return `number series ${v.series}`;
        case "acc_journal_entries": return `journal entry${v.entry_no ? ` ${v.series}-${v.entry_no}` : ""}`;
        case "acc_journal_lines": return "journal line";
        case "acc_attachments": return `attachment ${v.file_name}`;
        default: return row.table_name;
    }
}
const PLURAL = { acc_periods: "monthly periods", acc_party_roles: "roles", acc_accounts: "accounts", acc_bs_calendar: "calendar months", acc_tax_rates: "tax rates", acc_journal_lines: "journal lines" };
function groupSubject(items) {
    const t = items[0].table_name;
    const names = items.map(subject).reverse();   // oldest first
    const span = t === "acc_periods" || t === "acc_bs_calendar" ? `${names[0]} – ${names[names.length - 1]}` : names.join(", ");
    return `${items.length} ${PLURAL[t] || "records"} (${span})`;
}
function changes(row) {
    if (row.action !== "update" || !row.before || !row.after) return [];
    return Object.keys(row.after).filter(k => !NOISE.has(k) && JSON.stringify(row.before[k]) !== JSON.stringify(row.after[k]))
        .map(k => `${FIELD[k] || k.replace(/_/g, " ")}: ${fmtVal(k, row.before[k])} → ${fmtVal(k, row.after[k])}`);
}
const fmtVal = (k, v) => (k === "pan" && v ? "•••••" + String(v).slice(-2) : v === null || v === undefined || v === "" ? "—" : typeof v === "boolean" ? (v ? "yes" : "no") : String(v));

export default function ActivityLog({ ctx }) {
    const { data, mobile } = ctx;
    const [rows, setRows] = useState(null);
    const [err, setErr] = useState("");
    const [area, setArea] = useState("");
    const [limit, setLimit] = useState(200);
    const staffName = useMemo(() => Object.fromEntries(data.staff.map(s => [s.id, s.name])), [data.staff]);
    useEffect(() => {
        let alive = true;
        loadActivity({ limit }).then(r => alive && setRows(r)).catch(e => alive && setErr(errText(e)));
        return () => { alive = false; };
    }, [limit, data]);
    // Internal bookkeeping rows are hidden; rows written together by one action
    // (e.g. a fiscal year's 12 months) are shown as one line.
    const shown = useMemo(() => {
        const list = (rows || []).filter(r => r.table_name !== "acc_sequences" && (!area || AREA[r.table_name] === area));
        const groups = [];
        for (const r of list) {
            const g = groups[groups.length - 1];
            if (g && g.table_name === r.table_name && g.action === r.action && g.actor_staff_id === r.actor_staff_id && (g.reason || "") === (r.reason || "")
                && r.action === "insert" && Math.abs(new Date(g.at) - new Date(r.at)) < 3000) g.items.push(r);
            else groups.push({ ...r, items: [r] });
        }
        return groups;
    }, [rows, area]);
    return (
        <SectionCard title="Activity log" subtitle="Every change to the books, recorded automatically with who did it, when and why. Entries here can't be edited or deleted by anyone." pad={0}>
            <div style={{ padding: 12, display: "flex", gap: 8, flexWrap: "wrap", borderBottom: `1px solid ${T.borderSoft}` }}>
                <Select value={area} onChange={e => setArea(e.target.value)} style={{ width: mobile ? "100%" : 240 }}>
                    <option value="">Everything</option>
                    {[...new Set(Object.values(AREA))].map(a => <option key={a} value={a}>{a}</option>)}
                </Select>
            </div>
            {err && <div style={{ padding: 12 }}><Notice tone="danger">{err}</Notice></div>}
            {!rows && !err && <div style={{ padding: 12 }}><SkeletonTable rows={6} cols={3} card={false} /></div>}
            {rows && shown.length === 0 && <div style={{ padding: 16, ...text.caption }}>Nothing recorded yet.</div>}
            <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {shown.map(r => {
                    const ch = changes(r);
                    const tone = r.action === "delete" ? "review" : r.action === "post" || r.action === "lock" ? "finalized" : r.action === "reverse" || r.action === "unlock" ? "unverified" : "current";
                    return (
                        <li key={r.id} style={{ padding: "12px 16px", borderBottom: `1px solid ${T.borderSoft}` }}>
                            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                                <StatusBadge status={tone} label={VERB[r.action] || r.action} />
                                <span style={{ color: T.textStrong, fontSize: 14 }}>{r.items.length > 1 ? groupSubject(r.items) : subject(r)}</span>
                                <span style={text.caption}>· {AREA[r.table_name]}</span>
                            </div>
                            {ch.length > 0 && <div style={{ ...text.caption, marginTop: 4, overflowWrap: "anywhere" }}>{ch.join(" · ")}</div>}
                            {r.reason && <div style={{ fontSize: 13, color: T.text, marginTop: 4 }}>Reason: {r.reason}</div>}
                            <div style={{ ...text.caption, marginTop: 4 }}>{r.actor_staff_id ? staffName[r.actor_staff_id] || r.actor_staff_id : "System (setup)"} · {fmtDateTime(r.at)}</div>
                        </li>
                    );
                })}
            </ol>
            {rows && rows.length >= limit && <div style={{ padding: 12 }}><Button size="sm" variant="secondary" onClick={() => setLimit(l => l + 300)}>Show older activity</Button></div>}
        </SectionCard>
    );
}
